import { animate, style, transition, trigger } from '@angular/animations';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { GoogleTagManagerService } from '@app/core/lib/google-tag-manager/angular-google-tag-manager.service';
import { CookiesService } from '@app/core/services/cookies.service';
import { getProtocolHostnameAndPort } from '@app/shared/helpers/location';
import { ThemeSimplified } from '@app/shared/helpers/theme';
import { Options, SourceInfo } from 'plyr';
import { Constant } from 'src/constant';


@Component({
  selector: 'app-smash-reveal',
  templateUrl: './smash-reveal.component.html',
  styleUrls: ['./smash-reveal.component.scss'],
  animations: [
    trigger('teaserAnimation', [
      transition(":enter", [
        style({ opacity: 1 }),
        animate('100ms', style({ opacity: 1 })),
      ]),
      transition(":leave", [
        animate('500ms', style({ opacity: 0 }))
      ])
    ]),
    trigger('promotionApparition', [
      transition(":enter", [
        style({ opacity: 0 }),
        animate('500ms 500ms', style({ opacity: 1 })),
      ]),
    ]),
  ]
})
export class SmashRevealComponent implements OnInit, OnChanges {

  @Input() theme: ThemeSimplified;

  localIndexSelected = 0;
  timerNavigation = null;
  playerSource: SourceInfo;
  playerOptions: Options;
  isTeasingDisplayed = false;

  constructor(private gtmService: GoogleTagManagerService, private cookiesService: CookiesService) { }

  ngOnInit(): void { }

  ngOnChanges(changes: SimpleChanges) {
    if (!!changes?.theme?.currentValue) {
      clearTimeout(this.timerNavigation);
      this.replay();
      this.localIndexSelected = 0;
      this.buildMediaIfNecessary();
    }
  }

  isTeasingDisplayable() {
    return this.theme?.reveal?.teasing?.text || this.theme?.reveal?.teasing?.background?.url;
  }

  replay() {
    if (this.isTeasingDisplayable()) {
      this.isTeasingDisplayed = true;
    }
    this.localIndexSelected = 0;
    this.buildMediaIfNecessary();
    setTimeout(() => {
      this.isTeasingDisplayed = false;
    }, 5000);
  }

  buildMediaIfNecessary() {
    if (this.theme?.reveal?.medias?.length > 0) {
      const selectedMedia = this.theme.reveal.medias[this.localIndexSelected];
      if (selectedMedia?.type === 'Youtube' || selectedMedia?.type === 'Vimeo' || selectedMedia?.type === 'Video') {
        this.buildPlayer(selectedMedia);
        clearTimeout(this.timerNavigation);
      }
    }
  }

  navigateMediaLeft() {
    if (this.localIndexSelected > 0) {
      --this.localIndexSelected;
    } else {
      this.localIndexSelected = this.theme.reveal.medias.length - 1;
    }
    this.buildMediaIfNecessary();
  }

  navigateMediaRight() {
    if (this.localIndexSelected < this.theme.reveal.medias.length - 1) {
      ++this.localIndexSelected;
    } else {
      this.localIndexSelected = 0;
    }
    this.buildMediaIfNecessary();
  }

  initTimerImage() {
    clearTimeout(this.timerNavigation);
    this.timerNavigation = setTimeout(() => {
      this.navigateMediaRight();
    }, 5000);
  }

  pushGtmTag() {
    this.gtmService.pushTag({
      event: Constant.eventGTM.CLICK_THEME_REVEAL_CTA,
      theme_id: this.theme.id,
      theme_name: this.theme.name,
      theme_reveal_target: this.theme.target,
      theme_reveal_cta_url: this.theme.reveal.callToAction.url,
    });
  }

  redirectToCtaUrl() {
    window.open(getProtocolHostnameAndPort() + '/redirect?url=' + encodeURIComponent(this.theme.reveal.callToAction.url), '_blank');
  }

  onCtaClick() {
    this.redirectToCtaUrl();
    this.pushGtmTag();
  }

  // Player methods
  buildPlayer(media: ThemeSimplified['reveal']['medias'][0]) {
    this.playerSource = this.buildPlayerSource(media);
    this.playerOptions = this.buildPlayerOptions(media);
  }

  buildPlayerSource(media: ThemeSimplified['reveal']['medias'][0]): SourceInfo {
    const source: SourceInfo = {
      sources: [{
        src: media.url,
        provider: Constant.videoProviders[media.type],
      }],
      type: 'video',
    }
    return source;
  }

  buildPlayerOptions(media: ThemeSimplified['reveal']['medias'][0]): Options {
    const options: Options = {
      autoplay: true,
      loop: { active: this.theme.reveal.medias.length > 1 ? false : true },
      controls: ['play', 'progress', 'volume'],
      volume: 100,
      disableContextMenu: true,
      storage: { enabled: false }
    };

    options.controls = ['play', 'progress', 'volume', 'speed', 'duration', 'mute', 'settings', 'fullscreen'];

    if (media.type === 'Youtube') {
      options.youtube = { noCookie: true, rel: 0, controls: 0, showinfo: 0, iv_load_policy: 3, modestbranding: 1, origin: location.origin };
    }

    if (media.type === 'Vimeo') {
      options.vimeo = { dnt: 1, byline: false, portrait: false, title: false, speed: true, transparent: false };
    }

    return options;
  }
}
